<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useI18n } from 'vue-i18n';
import { isNotReadyStatus } from 'dashboard/helper/phoneHelper';

const store = useStore();
const { accountId } = useAccount();
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const customAgentPhoneStatuses = computed(
  () => currentAccount.value?.agent_phone_statuses || []
);

const contactUrl = contactId =>
  frontendURL(`accounts/${accountId.value}/contacts/${contactId}`);
const { t, te } = useI18n();

const phoneQueues = computed(() => store.getters['phoneQueues/getPhoneQueues']);
const uiFlags = computed(() => store.getters['phoneQueues/getUIFlags']);
const credentials = computed(
  () => store.getters['userPhoneCredentials/getCredentials']
);

const PICKUP_BLOCKED_STATUSES = [
  'OTHER_CALL',
  'QUEUE_CALL',
  'LOGGED_OUT',
  'OFFLINE',
];

const canPickupCall = computed(() => {
  const queueStatus = credentials.value?.queue_status;
  if (!queueStatus?.length) return false;
  return queueStatus.some(qs => !PICKUP_BLOCKED_STATUSES.includes(qs.status));
});

const currentTime = ref(Date.now());
let intervalId = null;

onMounted(async () => {
  await store.dispatch('phoneQueues/get');
  await store.dispatch('userPhoneCredentials/get');
  intervalId = setInterval(() => {
    currentTime.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});

const formatDuration = seconds => {
  if (seconds < 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const calculateDuration = statusChangedAt => {
  if (!statusChangedAt) return '0s';

  const now = currentTime.value;
  const changedAt = new Date(statusChangedAt).getTime();
  const seconds = Math.floor((now - changedAt) / 1000);

  return formatDuration(seconds);
};

const noTimerStatuses = ['READY', 'OFFLINE', 'ONLINE'];

const agentDurations = computed(() => {
  const durations = {};
  phoneQueues.value.forEach(queue => {
    queue.agents.forEach(agent => {
      if (!durations[agent.id]) {
        durations[agent.id] = noTimerStatuses.includes(agent.status)
          ? '0:00'
          : calculateDuration(agent.status_changed_at);
      }
    });
  });
  return durations;
});

const getOnCallCount = agents => {
  return agents.filter(agent =>
    ['OTHER_CALL', 'QUEUE_CALL'].includes(agent.status)
  ).length;
};

const getAvailableCount = agents => {
  return agents.filter(agent => !['ONLINE', 'OFFLINE'].includes(agent.status))
    .length;
};

const getStatusPriority = status => {
  // On call (highest priority)
  if (['OTHER_CALL', 'QUEUE_CALL'].includes(status)) return 1;
  // Paused (built-in not-ready states or any custom status configured on the account)
  if (isNotReadyStatus(status, customAgentPhoneStatuses.value)) return 2;
  // Ready and others
  return 3;
};

const queueStatusClass = status => {
  if (status === 'READY') return 'status-ready';
  if (['OTHER_CALL', 'QUEUE_CALL'].includes(status)) return 'status-on-call';
  if (['ONLINE', 'OFFLINE'].includes(status)) return 'status-offline';
  if (isNotReadyStatus(status, customAgentPhoneStatuses.value))
    return 'status-not-ready';
  return '';
};

const queueStatusLabel = status => {
  if (!status) return '';
  const key = `QUEUE_STATUS.${status}`;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return te(key) ? t(key) : status;
};

const pickupCall = async callerId => {
  try {
    await store.dispatch('phoneQueues/pickupCall', { callerId });
    useAlert(t('WALLBOARD.PICKUP_SUCCESS'));
  } catch {
    useAlert(t('WALLBOARD.PICKUP_ERROR'));
  }
};

const sortedQueueAgents = queue => {
  return [...queue.agents].sort((a, b) => {
    const priorityA = getStatusPriority(a.status);
    const priorityB = getStatusPriority(b.status);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, sort by name
    return (a.name || '').localeCompare(b.name || '');
  });
};
</script>

<template>
  <div class="flex flex-col text-n-slate-12 w-full h-full p-5">
    <div class="text-2xl font-semibold mb-2">
      {{ $t('SIDEBAR.WALLBOARD_QUEUES') }}
    </div>

    <div v-if="uiFlags.isFetching" class="text-n-slate-11">
      {{ $t('WALLBOARD.LOADING') }}
    </div>

    <div v-else-if="phoneQueues.length === 0" class="text-n-slate-11">
      {{ $t('WALLBOARD.NO_QUEUES') }}
    </div>

    <div v-else class="grid gap-6 overflow-auto queue-grid">
      <div
        v-for="queue in phoneQueues"
        :key="queue.id"
        class="border border-n-weak rounded-lg p-4 bg-n-solid-2 h-[fit-content]"
      >
        <div class="text-lg font-semibold mb-3">
          {{ queue.name }}
          (<template v-if="getAvailableCount(queue.agents) === 0">
            <span class="text-red-600 font-bold">
              {{ $t('WALLBOARD.NO_AVAILABLE', { count: 0 }) }}
            </span>
          </template>
          <template v-else>
            {{
              $t('WALLBOARD.AGENTS_COUNT', {
                onCall: getOnCallCount(queue.agents),
                total: queue.agents.length,
              })
            }}
          </template>
          <template v-if="queue.callers.length > 0">
            /
            {{
              $t('WALLBOARD.WAITING_CALLERS_COUNT', {
                count: queue.callers.length,
              })
            }} </template
          >)
        </div>

        <!-- Agents Section -->
        <div>
          <div>
            <div v-if="queue.agents.length === 0" class="text-n-slate-11">
              {{ $t('WALLBOARD.NO_AGENTS') }}
            </div>
            <div v-else class="table-wrapper">
              <div class="table-container">
                <table class="w-full">
                  <tbody class="divide-y divide-n-weak text-n-slate-11">
                    <tr
                      v-for="agent in sortedQueueAgents(queue)"
                      :key="agent.id"
                    >
                      <td
                        class="w-20 pr-1 py-0"
                        :class="queueStatusClass(agent.status)"
                      >
                        <div class="queue-status-bubble">
                          {{ queueStatusLabel(agent.status) }}
                        </div>
                      </td>
                      <td class="whitespace-nowrap w-20 text-right px-2 py-0">
                        {{ agentDurations[agent.id] }}
                      </td>
                      <td class="px-2 py-0">
                        {{ agent.name }}
                      </td>
                      <td class="pl-2 py-0">
                        {{ agent.extension || '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Callers Section -->
        <div class="mt-4">
          <div>
            <div v-if="queue.callers.length > 0" class="table-wrapper">
              <div class="table-container">
                <table class="w-full">
                  <thead>
                    <tr class="text-n-slate-11">
                      <th class="py-2 px-4 text-left font-medium">
                        {{ $t('WALLBOARD.CALLER') }}
                      </th>
                      <th class="py-2 px-4 text-left font-medium">
                        {{ $t('WALLBOARD.NUMBER') }}
                      </th>
                      <th class="py-2 px-4 text-left font-medium">
                        {{ $t('WALLBOARD.WAIT_TIME') }}
                      </th>
                      <th class="py-2 px-4 text-left font-medium">
                        {{ $t('WALLBOARD.ACTION') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-n-weak text-n-slate-11">
                    <tr v-for="caller in queue.callers" :key="caller.id">
                      <td>
                        <a
                          v-if="caller.contact_id"
                          :href="contactUrl(caller.contact_id)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-woot-500 hover:underline"
                        >
                          {{
                            caller.contact_name ||
                            caller.name ||
                            $t('WALLBOARD.UNKNOWN_CALLER')
                          }}
                        </a>
                        <span v-else>
                          {{ caller.name || $t('WALLBOARD.UNKNOWN_CALLER') }}
                        </span>
                      </td>
                      <td class="py-2 px-4">
                        {{ caller.number }}
                      </td>
                      <td class="py-2 px-4 whitespace-nowrap">
                        {{ calculateDuration(caller.status_changed_at) }}
                      </td>
                      <td class="py-2 px-4">
                        <span
                          v-tooltip.left="
                            !canPickupCall
                              ? $t('WALLBOARD.PICKUP_DISABLED_TOOLTIP')
                              : ''
                          "
                        >
                          <button
                            class="px-3 py-1 text-sm font-medium text-white bg-woot-500 rounded hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            :disabled="!canPickupCall"
                            @click="pickupCall(caller.id)"
                          >
                            {{ $t('WALLBOARD.PICKUP_CALL') }}
                          </button>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.queue-grid {
  grid-template-columns: repeat(auto-fit, minmax(30rem, 1fr));
}

.table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

.table-container {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border-weak);
  overflow: hidden;
  min-width: min-content;
}

.table-container table {
  table-layout: auto;
}

.table-container thead {
  background-color: var(--color-background-solid-2);
  border-bottom: 1px solid var(--color-border-weak);
}

.queue-status-bubble {
  @apply py-1 px-3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: white;
  cursor: default;
  min-width: 20px;
  text-align: center;
  white-space: nowrap;
}

.status-ready {
  background-color: #43b581; /* green */
}

.status-not-ready {
  background-color: #faa61a; /* yellow */
}

.status-on-call {
  background-color: #007bff; /* blue */
}

.status-offline {
  background-color: #f04747; /* red */
}

.status-duration {
  background-color: #6c757d; /* gray */
}
</style>
